/**
 * RegistrationContainer — public signup (manager/investor: credentials + T&C only);
 * admin self-signup remains BasicInfo → AdminInfo → summary + register.
 * @viewport both
 */
// src/features/auth/registration/RegistrationContainer.jsx
import React, { useState, useCallback, useEffect, useRef, lazy } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  Box,
  Button,
  Container,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { FONT_SIZE } from '@/core/platform/platformStandards';
import { PRODUCT_WALKTHROUGH_URL } from '@/core/constants/externalLinks';
import authService from '@/services/authService';
import lookupService from '@/services/lookupService';
import agreementService from '@/services/agreementService';
import useAuthConfig from '@/hooks/useAuthConfig';

import ErrorBoundary from '@/components/common/ErrorBoundary';
import HFRLogo from '@/components/common/HFRLogo';
import RegistrationPendingApproval from '@/features/auth/registration/steps/RegistrationPendingApproval';

const BasicInfo = lazy(() => import('@/features/auth/registration/steps/BasicInfo'));
const AdminInfo = lazy(() => import('@/features/auth/registration/steps/AdminInfo'));
const RegistrationSummary = lazy(() => import('@/features/auth/registration/steps/RegistrationSummary'));

const ADMIN_FLOW_LAST_STEP = 2;

const RegistrationContainer = ({ onRegistrationComplete }) => {
  const [searchParams] = useSearchParams();
  const registerMinimalInFlightRef = useRef(false);
  const { loading: configLoading } = useAuthConfig();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  const [emailValidation, setEmailValidation] = useState({
    isChecking: false,
    isValid: false,
    exists: false,
    isSpecialDomain: false,
    message: ''
  });

  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);

  const updateData = useCallback((newData) => {
    setFormData((prevData) => ({
      ...prevData,
      ...newData
    }));
    setError(null);
  }, []);

  useEffect(() => {
    document.body.classList.add('auth-page');
    return () => {
      document.body.classList.remove('auth-page');
    };
  }, []);

  // Marketing invite links pre-select manager vs allocator registration.
  useEffect(() => {
    const roleParam = (searchParams.get('role') || '').toLowerCase();
    if (roleParam === 'manager' || roleParam === 'investor') {
      setFormData((prev) => ({ ...prev, role: roleParam }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (executeRecaptcha) {
      setRecaptchaReady(true);
    } else {
      setRecaptchaReady(false);
    }
  }, [executeRecaptcha]);

  const goToNextStep = useCallback((newData) => {
    if (newData) {
      setFormData((prevData) => ({
        ...prevData,
        ...newData
      }));
    }
    setCurrentStep((prev) => Math.min(prev + 1, ADMIN_FLOW_LAST_STEP));
    setError(null);
  }, []);

  const goToPreviousStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    setError(null);
  }, []);

  const scheduleTermsPdfUpload = useCallback((userResult, meta) => {
    const regPdfUploadUrls = userResult?.pdf_upload_urls || {};
    const urlInfo = regPdfUploadUrls.terms_and_conditions;
    if (!urlInfo?.upload_url) return;
    (async () => {
      try {
        const [{ renderAgreementPDFBlob }, { default: AgreementPDFDocument }] = await Promise.all([
          import('@/utils/pdfGenerator'),
          import('@/features/dashboard/manager/settings/components/AgreementPDFDocument')
        ]);
        const agreementData = {
          type: 'terms',
          text: '',
          metadata: {
            accepted_at: meta.agreement_accepted_at || new Date().toISOString(),
            version: meta.agreement_version || '1.0'
          }
        };
        const blob = await renderAgreementPDFBlob(<AgreementPDFDocument agreementData={agreementData} />);
        await agreementService.uploadSignedPdf(urlInfo.upload_url, blob);
      } catch (err) {
        console.warn('Registration T&C PDF upload failed (non-fatal):', err);
      }
    })().catch(() => {});
  }, []);

  const obtainRecaptchaToken = useCallback(async () => {
    if (!executeRecaptcha) {
      throw new Error('reCAPTCHA is not ready. Please refresh the page and try again.');
    }
    const recaptchaToken = await Promise.race([
      executeRecaptcha('register'),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('reCAPTCHA timeout')), 5000)
      )
    ]);
    if (!recaptchaToken || typeof recaptchaToken !== 'string' || recaptchaToken.length < 10) {
      throw new Error('reCAPTCHA token generation returned empty value');
    }
    return recaptchaToken;
  }, [executeRecaptcha]);

  const registerMinimalManagerInvestor = useCallback(async (merged) => {
    if (registerMinimalInFlightRef.current) {
      return;
    }
    registerMinimalInFlightRef.current = true;
    setIsSubmitting(true);
    setError(null);
    try {
      const recaptchaToken = await obtainRecaptchaToken();
      const userRegistrationData = {
        firstName: merged.firstName,
        lastName: merged.lastName,
        email: merged.email,
        password: merged.password,
        role: merged.role,
        agreement_accepted: merged.agreement_accepted === true,
        agreement_accepted_at: merged.agreement_accepted_at || new Date().toISOString(),
        agreement_version: merged.agreement_version || '1.0',
        email_verification_token: merged.emailVerificationToken ?? null,
      };
      const userResult = await authService.register(userRegistrationData, recaptchaToken);
      scheduleTermsPdfUpload(userResult, merged);

      const apiUser = userResult.user || {};
      const hasSessionTokens = Boolean(
        userResult.accessToken || userResult.token || userResult.refreshToken
      );

      if (hasSessionTokens && typeof onRegistrationComplete === 'function') {
        try {
          await onRegistrationComplete(userResult);
        } catch (e) {
          console.warn('onRegistrationComplete failed:', e);
        }
        return;
      }

      setRegistrationComplete(true);
      setRegistrationData({
        ...apiUser,
        firstName: apiUser.firstName ?? merged.firstName,
        lastName: apiUser.lastName ?? merged.lastName,
        email: apiUser.email ?? merged.email,
        user_id: userResult.user_id,
        firm_id: null,
        entity: userResult.entity || null,
        entityCreated: Boolean(userResult.entity),
        logoStatus: userResult.logo_status || null,
      });
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'An unexpected error occurred during registration');
    } finally {
      setIsSubmitting(false);
      registerMinimalInFlightRef.current = false;
    }
  }, [obtainRecaptchaToken, onRegistrationComplete, scheduleTermsPdfUpload]);

  const handleBasicInfoNext = useCallback((payload) => {
    setFormData((prev) => ({ ...prev, ...payload }));
    if (payload.role === 'admin') {
      setCurrentStep(1);
      return;
    }
    void registerMinimalManagerInvestor(payload);
  }, [registerMinimalManagerInvestor]);

  const handleAdminRegistrationSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      const convertDisplayToLookupValue = (displayValue, lookupType) => {
        if (!displayValue) return displayValue;
        try {
          const lookupOptions = lookupService.getOptions(lookupType);
          const foundOption = lookupOptions.find((option) =>
            option.label === displayValue ||
            option.display === displayValue ||
            option.value === displayValue
          );
          return foundOption ? foundOption.value : displayValue;
        } catch (lookupErr) {
          console.warn(`Error converting ${lookupType} lookup for value: ${displayValue}`, lookupErr);
          return displayValue;
        }
      };

      const userRegistrationData = { ...formData };
      if (formData.organizationType) {
        userRegistrationData.department = convertDisplayToLookupValue(formData.organizationType, 'firm_types');
      }
      if (formData.position) {
        userRegistrationData.jobTitle = convertDisplayToLookupValue(formData.position, 'job_titles');
      }

      const recaptchaToken = await obtainRecaptchaToken();
      const userResult = await authService.register(userRegistrationData, recaptchaToken);
      scheduleTermsPdfUpload(userResult, formData);

      const apiUser = userResult.user || {};
      const hasSessionTokens = Boolean(
        userResult.accessToken || userResult.token || userResult.refreshToken
      );

      if (hasSessionTokens && typeof onRegistrationComplete === 'function') {
        try {
          await onRegistrationComplete(userResult);
        } catch (e) {
          console.warn('onRegistrationComplete failed:', e);
        }
        return;
      }

      setRegistrationComplete(true);
      setRegistrationData({
        ...apiUser,
        firstName: apiUser.firstName ?? formData.firstName,
        lastName: apiUser.lastName ?? formData.lastName,
        email: apiUser.email ?? formData.email,
        user_id: userResult.user_id,
        firm_id: null,
        entity: userResult.entity || null,
        entityCreated: Boolean(userResult.entity),
        logoStatus: userResult.logo_status || null,
      });
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'An unexpected error occurred during registration');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, obtainRecaptchaToken, onRegistrationComplete, scheduleTermsPdfUpload]);

  const handleErrorReset = useCallback(() => {
    setError(null);
    setCurrentStep(0);
    setFormData({});
  }, []);

  const renderCurrentStep = useCallback(() => {
    const basicProps = {
      data: formData,
      updateData,
      onNext: handleBasicInfoNext,
      onBack: goToPreviousStep,
      isSubmitting,
      recaptchaReady,
      emailValidation,
      updateEmailValidation: setEmailValidation
    };

    const adminStepProps = {
      data: formData,
      updateData,
      onNext: goToNextStep,
      onBack: goToPreviousStep,
      onSubmit: handleAdminRegistrationSubmit,
      isSubmitting,
      recaptchaReady,
      emailValidation,
      updateEmailValidation: setEmailValidation
    };

    const getStepComponent = () => {
      if (currentStep === 0) {
        return <BasicInfo {...basicProps} />;
      }
      if (formData.role === 'admin' && currentStep === 1) {
        return (
          <AdminInfo
            data={formData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
          />
        );
      }
      if (formData.role === 'admin' && currentStep === 2) {
        return <RegistrationSummary {...adminStepProps} />;
      }
      return <BasicInfo {...basicProps} />;
    };

    return (
      <ErrorBoundary
        onError={(boundaryError, errorInfo) => {
          console.error('Step component error:', boundaryError, errorInfo);
          setError('A component error occurred. Please try again.');
        }}
        onReset={handleErrorReset}
        resetButtonText="Restart Registration"
      >
        <React.Suspense
          fallback={(
            <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, md: 4 } }}>
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          )}
        >
          {getStepComponent()}
        </React.Suspense>
      </ErrorBoundary>
    );
  }, [
    currentStep,
    formData,
    updateData,
    handleBasicInfoNext,
    goToNextStep,
    goToPreviousStep,
    handleAdminRegistrationSubmit,
    isSubmitting,
    recaptchaReady,
    handleErrorReset,
    emailValidation
  ]);

  if (configLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography variant="body2" color="text.secondary">
            Loading registration options...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (registrationComplete && registrationData) {
    const st = (registrationData.approval_status || registrationData.status || '').toLowerCase();
    if (st === 'pending') {
      return (
        <Container maxWidth="md">
          <RegistrationPendingApproval userData={registrationData} />
        </Container>
      );
    }
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Account created
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your account was created. Sign in with your email and password to continue.
          </Typography>
          <Button variant="contained" href="/login" component="a">
            Go to sign in
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ px: { xs: 2, md: 3 } }}>
      <div className="registration-container">
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ width: { xs: '100%', md: '100%' }, maxWidth: { xs: '100%', md: 600 }, mx: 'auto' }}
        >
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', py: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <HFRLogo height={100} />
            <Typography variant="h4" align="center" sx={{ fontSize: { xs: FONT_SIZE.xxl, md: FONT_SIZE.xxxl } }}>
              Create Your Account
            </Typography>
          </Box>

          {error && (
            <Box
              sx={{
                backgroundColor: 'error.light',
                color: 'error.contrastText',
                borderBottom: '1px solid',
                borderColor: 'error.main',
                p: { xs: 1.5, md: 2 }
              }}
            >
              <Typography variant="body2" align="center">
                {error}
              </Typography>
            </Box>
          )}

          <Box sx={{ p: { xs: 2, md: 4 } }}>
            {renderCurrentStep()}
          </Box>
        </Paper>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Questions about signing up?{' '}
            <Link
              component={RouterLink}
              to="/help/faq"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              View FAQ
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: FONT_SIZE.md, mt: 1 }}>
            Explore first?{' '}
            <Link
              href={PRODUCT_WALKTHROUGH_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Demo
            </Link>
          </Typography>
        </Box>
      </div>
    </Container>
  );
};

export default React.memo(RegistrationContainer);
